import { useCallback, useRef, MutableRefObject, useEffect } from "react";

export const SCROLL_ID_ATTRIBUTE = "data-scroll-id";

export interface ScrollAutoSelectOptions<T extends string> {
  /** List of HTML element ids to be observed. Element ids must be in the order they
   * appear in the document and will need to have these ids set as a "data-scroll-id"
   * attribute on the corresponding HTML element */
  elements: T[];
  enabled: boolean;
  /** Callback to fire when a new element crosses the scroll threshold */
  onCrossThreshold: (elementId: T) => void;
  scrollContainer: MutableRefObject<HTMLElement | null>;
  /** DOM position relative to scroll container at which to trigger "selected"
   * element change */
  threshold: number;
}

/**
 * Hook that will listen to scroll events and auto-select elements (via callback)
 * as they pass through the scroll threshold
 */
const useScrollAutoSelect = <T extends string>({
  enabled,
  elements,
  onCrossThreshold,
  scrollContainer,
  threshold,
}: ScrollAutoSelectOptions<T>) => {
  const lastIntersectingElementId = useRef<T | null>(null);

  const scrollListener = useCallback(() => {
    const domElements = Array.from(
      document.querySelectorAll(`[${SCROLL_ID_ATTRIBUTE}]`)
    );

    const parentElementBoundingBox =
      scrollContainer.current?.getBoundingClientRect();

    if (!parentElementBoundingBox) return;

    const scrollThreshold = parentElementBoundingBox.top + threshold;

    const intersectingElement = domElements.find((el) => {
      const boundingRect = el?.getBoundingClientRect();
      if (!boundingRect) return false;
      return (
        boundingRect.top < scrollThreshold &&
        boundingRect.bottom >= scrollThreshold
      );
    });

    const elementId = intersectingElement?.getAttribute(
      SCROLL_ID_ATTRIBUTE
    ) as T;

    if (elementId && elementId !== lastIntersectingElementId.current) {
      lastIntersectingElementId.current = elementId;
      onCrossThreshold(elementId);
    }
  }, [threshold, elements, onCrossThreshold]);

  useEffect(() => {
    if (!enabled) {
      scrollContainer.current?.removeEventListener("scroll", scrollListener);
      return;
    }

    scrollContainer.current?.addEventListener("scroll", scrollListener);

    return () =>
      scrollContainer.current?.removeEventListener("scroll", scrollListener);
  }, [enabled, scrollListener]);
};

export default useScrollAutoSelect;
